import { ROUTES } from './generated/routes';
/** Fills `{param}` placeholders in a contract path. */
function route(name, params = {}) {
    const definition = ROUTES[name];
    return definition.path.replace(/\{(\w+)\}/g, (_match, key) => {
        const value = params[key];
        if (value === undefined) {
            throw new Error(`Route ${name} is missing the "${key}" parameter.`);
        }
        return encodeURIComponent(value);
    });
}
/** Turns a typed query object into the client's loose query bag. */
function query(input) {
    if (!input)
        return undefined;
    const output = {};
    for (const [key, value] of Object.entries(input)) {
        // A list travels comma-separated, the form every list filter accepts.
        if (Array.isArray(value)) {
            if (value.length > 0)
                output[key] = value.join(',');
        }
        else if (value !== undefined && value !== null && value !== '') {
            output[key] = value;
        }
    }
    return output;
}
/**
 * The API surface, grouped the way the storefront thinks about it.
 *
 * Every method names a route from the generated table rather than a string
 * literal, so a contract change is a compile error here instead of a 404 in a
 * customer's browser (ADR-016).
 */
export class EcomApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** Returns a copy bound to a locale — the client then sends it on every call. */
    forLocale(locale) {
        return new EcomApi(this.client.withLocale(locale));
    }
    catalog = {
        brands: (params, options) => this.client.requestList('GET', route('storefront_brands'), {
            query: query(params),
            ...options,
        }),
        /** The colour facet for the listing rail — every colour an active variant wears, with counts. */
        colors: (options) => this.client.requestList('GET', route('storefront_colors'), options),
        /** «أعلمني عند التوفّر»: one note when a sold-out variant is back. Guests pass an email. */
        stockAlert: (productId, body, options) => this.client.request('POST', route('stockAlerts_request', { productId }), {
            ...options,
            body,
        }),
        /** Whether the checkout offers WhatsApp order updates (ADR-105). */
        whatsapp: (options) => this.client.request('GET', route('whatsApp_storefront'), options),
        /** The analytics tags this store has switched on — public ids only. */
        analyticsTags: (options) => this.client.requestList('GET', route('analytics_tags'), options),
        /** Whether this store takes tickets, and the reasons it offers. */
        supportConfig: (locale, options) => this.client.request('GET', route('support_config'), {
            query: query({ locale }),
            ...options,
        }),
        /**
         * Looks (R17): the ones holding a product — its page — or every active one,
         * each priced from its lines. Empty while the store has looks switched off.
         */
        looks: (params = {}, fetchOptions) => this.client.requestList('GET', route('looks_storefront'), { query: query(params), fetchOptions }),
        /** Every link worth indexing, with when it last changed — the sitemap's source (ADR-095). */
        sitemap: (fetchOptions) => this.client.request('GET', route('seo_sitemap'), { fetchOptions }),
        /** A marketing mail's picture loaded (ADR-099). */
        mailOpen: (id) => this.client.request('POST', route('mailTracking_open'), { body: { id } }),
        /** A marketing mail's link followed: where it leads, if the store signed it (ADR-099). */
        mailClick: (body) => this.client.request('POST', route('mailTracking_click'), { body }),
        /** The live popups in one language (ADR-098); the storefront decides which fits. */
        popups: (locale, fetchOptions) => this.client.requestList('GET', route('storefrontPopups_list'), { query: { locale }, fetchOptions }),
        /** A popup seen or its button pressed — counted, never in the way. */
        popupEvent: (id, type) => this.client.request('POST', route('storefrontPopups_event', { id }), { body: { type } }),
        /** A newsletter popup's signup: the newsletter's consent mail, and the popup's code back. */
        popupSignup: (id, body) => this.client.request('POST', route('storefrontPopups_signup', { id }), { body }),
        /** One channel's product feed in one language (ADR-096); a channel switched off is a 404. */
        feed: (channel, locale, fetchOptions) => this.client.request('GET', route('feeds_feed', { channel }), { query: { locale }, fetchOptions }),
        /** The store's blog (ADR-080): live posts, newest first, paged by cursor. */
        blogPosts: (params = {}, fetchOptions) => this.client.requestList('GET', route('blog_published'), { query: query(params), fetchOptions }),
        /** One live post, with the products it carries that are on sale. */
        blogPost: (slug, locale, fetchOptions) => this.client.request('GET', route('blog_post', { slug }), { query: query({ locale }), fetchOptions }),
        /** Sets holding a product, each at its own price (ADR-078) — its page shows them, added in one step. */
        bundles: (params, fetchOptions) => this.client.requestList('GET', route('bundles_list'), { query: query(params), fetchOptions }),
        /** What the store's trade application asks — its lists, in this language (ADR-046). */
        tradeConfig: (locale, options) => this.client.request('GET', route('tradeApplications_config'), {
            query: query({ locale }),
            ...options,
        }),
        /** What this store accepts — rendered rather than hard-coded (ADR-015). */
        paymentMethods: (options) => this.client.requestList('GET', route('paymentMethods_methods'), options),
        categories: (options) => this.client.request('GET', route('storefront_categories'), {
            query: query({ search: options?.search }),
            fetchOptions: options?.fetchOptions,
        }),
        products: (params = {}, fetchOptions) => this.client.requestList('GET', route('storefront_products'), {
            query: query(params),
            fetchOptions,
        }),
        /**
         * "You may also love" (R15): what staff picked to go with these products,
         * then their categories — never one of them. Ask with the product on the
         * page, or with everything in the basket.
         */
        relatedProducts: (productIds, params = {}, fetchOptions) => this.client.requestList('GET', route('storefront_related'), {
            query: query({ productIds: productIds.join(','), ...params }),
            fetchOptions,
        }),
        /**
         * A listing's filter counts (ADR-102): the same query as `products`, answered
         * with every filter group's counts, the sub-categories, stock and price range.
         */
        facets: (params = {}, fetchOptions) => this.client.request('GET', route('storefront_facets'), {
            query: query(params),
            fetchOptions,
        }),
        /** Search as you type (ADR-101): products, categories and brands for the header (server-side, caller debounces). */
        suggest: (q, locale, fetchOptions) => this.client.request('GET', route('storefront_suggest'), {
            query: query({ q, locale }),
            fetchOptions,
        }),
        product: (slug, fetchOptions) => this.client.request('GET', route('storefront_product', { slug }), {
            fetchOptions,
        }),
    };
    settings = {
        /** Public subset only — store identity, tax display, flags, home layout. */
        public: (fetchOptions) => this.client.request('GET', route('settings_publicSettings'), { fetchOptions }),
        /** A home page draft behind its preview link (ADR-056): images as URLs, like `public`. */
        homePreview: (token, fetchOptions) => this.client.request('GET', route('settings_homePreview', { token }), {
            fetchOptions,
        }),
    };
    reviews = {
        /** Approved reviews, filtered to one star and sorted as asked (R8). */
        forProduct: (productId, params, options) => this.client.requestList('GET', route('reviews_forProduct', { productId }), { ...options, query: query(params) }),
        /** The average, the count and how many gave each star (R8). */
        summary: (productId, options) => this.client.request('GET', route('reviews_summary', { productId }), options),
        /** "Helpful", once per signed-in customer; `unvote` takes it back (R8). */
        vote: (reviewId, options) => this.client.request('POST', route('reviews_vote', { id: reviewId }), options),
        unvote: (reviewId, options) => this.client.request('DELETE', route('reviews_unvote', { id: reviewId }), options),
        create: (productId, body, options) => this.client.request('POST', route('reviews_create', { productId }), { ...options, body }),
        /**
         * The caller's own reviews, moderation state included.
         *
         * Paginated like its siblings: the endpoint has taken `limit` and `cursor`
         * since it shipped, and a method that cannot pass them makes an account
         * with more than a page of reviews unreachable past the first twenty.
         */
        mine: (params, options) => this.client.requestList('GET', route('reviews_mine'), {
            ...options,
            query: query(params),
        }),
    };
    /**
     * Questions about a product and the store's answers (R20, ADR-047). Only
     * answered ones are public; a signed-in reader's own waiting questions come
     * first on the first page, marked `mine`. `meta.total` counts the answered.
     */
    questions = {
        forProduct: (productId, params, options) => this.client.requestList('GET', route('questions_forProduct', { productId }), {
            ...options,
            query: query(params),
        }),
        /** Signed-in only; it waits for the store's answer. */
        ask: (productId, body, options) => this.client.request('POST', route('questions_ask', { productId }), { ...options, body }),
        /** The caller's own questions, every state — "Your questions". */
        mine: (params, options) => this.client.requestList('GET', route('questions_mine'), { ...options, query: query(params) }),
    };
    cart = {
        view: (options) => this.client.request('GET', route('cart_view'), options),
        addItem: (body, options) => this.client.request('POST', route('cart_addItem'), { ...options, body }),
        /** "Buy now" (ADR-086): one line in a basket of its own; its token goes back as `expressToken`. */
        express: (body, options) => this.client.request('POST', route('cart_express'), { ...options, body }),
        /** A "buy now" basket, by its token. */
        viewExpress: (expressToken, options) => this.client.request('GET', route('cart_viewExpress'), { ...options, expressToken }),
        /** Several lines in one step — a look (R17): all of them, or none. */
        addItems: (items, options) => this.client.request('POST', route('cart_addItems'), { ...options, body: { items } }),
        updateItem: (itemId, quantity, options) => this.client.request('PATCH', route('cart_updateItem', { itemId }), {
            ...options,
            body: { quantity },
        }),
        removeItem: (itemId, options) => this.client.request('DELETE', route('cart_removeItem', { itemId }), options),
        clear: (options) => this.client.request('DELETE', route('cart_clear'), options),
        /**
         * Validates a code against this cart. The API does not store it — the
         * caller keeps it and presents it as `discountCode` on later requests, which
         * `ClientOptions.defaultQuery` exists to do.
         */
        applyDiscount: (code, options) => this.client.request('POST', route('cart_applyDiscount'), { ...options, body: { code } }),
    };
    checkout = {
        /** What the order would cost, plus the shipping options for this address. */
        quote: (body, options) => this.client.request('POST', route('orders_quote'), { ...options, body }),
        place: (body, options) => this.client.request('POST', route('orders_placeOrder'), { ...options, body }),
    };
    orders = {
        /** Guest lookup: order number + the email it was placed with. */
        lookup: (body, options) => this.client.request('POST', route('orders_lookup'), { ...options, body }),
        /** Paginated, and searchable — the endpoint has taken `search` since it shipped. */
        mine: (params, options) => this.client.requestList('GET', route('orders_myOrders'), {
            ...options,
            query: query(params),
        }),
        one: (id, options) => this.client.request('GET', route('orders_myOrder', { id }), options),
        /** The tax invoice for the customer's own order, scoped by owner server-side. */
        invoice: (id, options) => this.client.download(route('invoicing_myInvoice', { id }), options),
        /** One of the customer's own documents — its invoice or a credit note — by the id `documents` lists (ADR-067). */
        document: (documentId, options) => this.client.download(route('invoicing_myDocument', { id: documentId }), options),
    };
    returns = {
        request: (body, options) => this.client.request('POST', route('returns_request'), { ...options, body }),
        mine: (params, options) => this.client.requestList('GET', route('returns_myReturns'), {
            ...options,
            query: query(params),
        }),
        one: (id, options) => this.client.request('GET', route('returns_myReturn', { id }), options),
        cancel: (id, options) => this.client.request('PATCH', route('returns_cancel', { id }), options),
    };
    auth = {
        /** How this store lets customers in — the sign-in page renders from it (ADR-015). */
        options: (options) => this.client.request('GET', route('authOptions_options'), options),
        /** A session — or, for staff with a second step due, a challenge to answer with a code (ADR-116). */
        login: (body, options) => this.client.request('POST', route('auth_login'), { ...options, body }),
        /** The second step: the challenge and a code from the app, or a recovery code. */
        verifyTwoFactor: (body, options) => this.client.request('POST', route('auth_verifyTwoFactor'), { ...options, body }),
        /** Where the store requires a second step and this person has none: the secret to scan. */
        setUpTwoFactor: (challenge, options) => this.client.request('POST', route('auth_setUpTwoFactor'), { ...options, body: { challenge } }),
        /** …its first code: on, the recovery codes, and the session. */
        enableTwoFactor: (body, options) => this.client.request('POST', route('auth_enableTwoFactor'), { ...options, body }),
        /** Sign in with Google or Apple: the ID token they posted back, and the nonce sent with the request (ADR-117). */
        social: (body, options) => this.client.request('POST', route('auth_socialSignIn'), { ...options, body }),
        register: (body, options) => this.client.request('POST', route('auth_register'), { ...options, body }),
        refresh: (refreshToken, options) => this.client.request('POST', route('auth_refresh'), {
            ...options,
            body: { refreshToken },
        }),
        logout: (refreshToken, options) => this.client.request('POST', route('auth_logout'), { ...options, body: { refreshToken } }),
        me: (options) => this.client.request('GET', route('auth_me'), options),
    };
    /** Signing in with a one-time code, where the store offers it (ADR-030). */
    otp = {
        request: (phone, options) => this.client.request('POST', route('auth_requestOtp'), { ...options, body: { phone } }),
        verify: (phone, code, options) => this.client.request('POST', route('auth_verifyOtp'), {
            ...options,
            body: { phone, code },
        }),
    };
    /** The way out of a campaign (ADR-036). No sign-in — that is the point. */
    unsubscribe = {
        unsubscribe: (body) => this.client.request('POST', route('unsubscribe_unsubscribe'), { body }),
    };
    account = {
        profile: (options) => this.client.request('GET', route('account_profile'), options),
        /** Everything this store holds about me (R10, PDPL). */
        exportData: (locale, options) => this.client.request('GET', route('privacy_exportOwn'), { ...options, query: query({ locale }) }),
        /** Erases my account, confirmed with my password or a code to my number (R10). */
        erase: (body, options) => this.client.request('POST', route('privacy_eraseOwn'), { ...options, body }),
        updateProfile: (
        /** `email` adds one to an account that has none (ADR-039); an existing one is kept. */
        body, options) => this.client.request('PATCH', route('account_updateProfile'), { ...options, body }),
        changePassword: (body, options) => this.client.request('POST', route('account_changePassword'), { ...options, body }),
        addresses: (options) => this.client.requestList('GET', route('account_addresses'), options),
        /** My store credit (ADR-081). */
        wallet: (options) => this.client.request('GET', route('wallet_mine'), options),
        /** Its movements, newest first, paged by cursor. */
        walletEntries: (params, options) => this.client.requestList('GET', route('wallet_myEntries'), { ...options, query: query(params) }),
        /** My affiliate figures (ADR-084) — not found unless the store made this account an affiliate. */
        affiliate: (options) => this.client.request('GET', route('affiliates_mine'), options),
        affiliateCommissions: (params, options) => this.client.requestList('GET', route('affiliates_myCommissions'), { ...options, query: query(params) }),
        /** My points and the store's terms (ADR-083). */
        loyalty: (options) => this.client.request('GET', route('loyalty_mine'), options),
        /** My invitation for friends (ADR-114): made the first time it is asked for. */
        referral: (options) => this.client.request('GET', route('referrals_mine'), options),
        loyaltyEntries: (params, options) => this.client.requestList('GET', route('loyalty_myEntries'), { ...options, query: query(params) }),
        /** Points into store credit, at the store's value. */
        convertPoints: (points, options) => this.client.request('POST', route('loyalty_convert'), { ...options, body: { points } }),
        /** A gift card's whole value into my store credit (ADR-082). */
        redeemGiftCard: (code, options) => this.client.request('POST', route('giftCards_redeem'), { ...options, body: { code } }),
        createAddress: (body, options) => this.client.request('POST', route('account_createAddress'), { ...options, body }),
        updateAddress: (id, body, options) => this.client.request('PATCH', route('account_updateAddress', { id }), { ...options, body }),
        tickets: (params, options) => this.client.requestList('GET', route('support_mine'), {
            ...options,
            query: query(params),
        }),
        ticket: (id, locale, options) => this.client.request('GET', route('support_ticket', { id }), {
            ...options,
            query: query({ locale }),
        }),
        openTicket: (body, locale, options) => this.client.request('POST', route('support_open'), {
            ...options,
            body,
            query: query({ locale }),
        }),
        replyToTicket: (id, body, locale, options) => this.client.request('POST', route('support_replyAsCustomer', { id }), {
            ...options,
            body: { body },
            query: query({ locale }),
        }),
        /** My trade account, if any, and whether the store takes trade customers (ADR-045). */
        trade: (locale, options) => this.client.request('GET', route('tradeAccount_mine'), { ...options, query: query({ locale }) }),
        /** Apply for a trade account, its documents uploaded first with `uploadTradeDocument` (ADR-046). */
        applyForTrade: (body, locale, options) => this.client.request('POST', route('tradeApplications_submit'), {
            ...options,
            body,
            query: query({ locale }),
        }),
        /**
         * A paper for a trade application — a CR certificate, a VAT certificate —
         * sent from the browser as it is picked; the answer's id goes in the
         * application's `documents`. Private: its url is a signed API path.
         */
        uploadTradeDocument: (file, fileName) => {
            const form = new FormData();
            form.append('purpose', 'trade_document');
            form.append('file', file, fileName);
            return this.client.requestMultipart('POST', route('shopperFiles_upload'), form);
        },
        /** My trade prices for the products on screen; empty at retail. Never cache it. */
        tradePrices: (productIds, options) => this.client.requestList('GET', route('tradeAccount_prices'), {
            ...options,
            query: query({ productIds: productIds.join(',') }),
        }),
        deleteAddress: (id, options) => this.client.request('DELETE', route('account_deleteAddress', { id }), options),
    };
    /**
     * The wishlist and the compare list (ADR-038).
     *
     * Public like the cart: a guest is identified by the token the first save
     * hands back, and signing in merges that list into the account's. Every call
     * returns the whole list, so a screen re-renders from one object rather than
     * guessing what a mutation did to it.
     */
    saved = {
        list: (kind, locale, options) => this.client.request('GET', route('savedProducts_list', { kind }), {
            ...options,
            query: query({ locale }),
        }),
        save: (kind, productId, locale, options) => this.client.request('POST', route('savedProducts_add', { kind }), {
            ...options,
            body: { productId },
            query: query({ locale }),
        }),
        unsave: (kind, productId, locale, options) => this.client.request('DELETE', route('savedProducts_remove', { kind, productId }), { ...options, query: query({ locale }) }),
        clear: (kind, locale, options) => this.client.request('DELETE', route('savedProducts_clear', { kind }), {
            ...options,
            query: query({ locale }),
        }),
    };
    /**
     * The newsletter box (ADR-040): nothing counts until the address confirms,
     * and the answer never says what was already there.
     */
    newsletter = {
        subscribe: (body, options) => this.client.request('POST', route('subscribers_subscribe'), { ...options, body }),
        confirm: (token, options) => this.client.request('POST', route('subscribers_confirm'), { ...options, body: { token } }),
    };
    /**
     * Writing to the store without an account (ADR-041). The link to the thread
     * goes to the inbox, never back here; its token is sent per call.
     */
    support = {
        write: (body, locale, options) => this.client.request('POST', route('guestSupport_open'), {
            ...options,
            body,
            query: query({ locale }),
        }),
        thread: (ticketToken, locale, options) => this.client.request('GET', route('guestSupport_thread'), {
            ...options,
            ticketToken,
            query: query({ locale }),
        }),
        reply: (ticketToken, body, locale, options) => this.client.request('POST', route('guestSupport_reply'), {
            ...options,
            ticketToken,
            body: { body },
            query: query({ locale }),
        }),
        resendLink: (body, options) => this.client.request('POST', route('guestSupport_resend'), { ...options, body }),
        /**
         * A file for a ticket, uploaded before the ticket is sent (ADR-044): the
         * answer's id goes in `attachmentIds`. Private — its url is a signed API path.
         */
        uploadFile: (file, fileName) => {
            const form = new FormData();
            form.append('purpose', 'ticket_attachment');
            form.append('file', file, fileName);
            return this.client.requestMultipart('POST', route('shopperFiles_upload'), form);
        },
    };
    geography = {
        countries: (params) => this.client.requestList('GET', route('geography_countries'), { query: query(params) }),
        regions: (countryId, params) => this.client.requestList('GET', route('geography_regions', { countryId }), {
            query: query(params),
        }),
        cities: (regionId, params) => this.client.requestList('GET', route('geography_cities', { regionId }), {
            query: query(params),
        }),
    };
    /** "Order today — delivered …" (R6): null when no delivery method has timing. */
    delivery = {
        estimate: (params, options) => this.client.request('GET', route('storefrontDelivery_estimate'), {
            ...options,
            query: query(params),
        }),
    };
}
//# sourceMappingURL=api.js.map